import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { 
  ConversationMode, 
  ConversationStepMode, 
  CreateConversationParams, 
  ConversationResponse,
  Message,
  ConversationState
} from '../../types/conversation.types';

// Export the state type for external use
export type ConversationSliceState = ConversationState;

const initialState: ConversationState = {
  session_id: null,
  mode: 'clarify',
  init_message: '',
  name: '',
  messages: [],
  isLoading: false,
  isActive: false,
  conversationId: undefined,
  initialAttachmentIds: undefined,
  skip_analysis: false,
  isOnboardingPrototyper: false,
  llmFamily: 'Gemini',
  initiatedFromUncover: false,
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    // Create new conversation (synchronous action matching old implementation)
    createNewConversation: (
      state,
      action: PayloadAction<{
        init_message: string;
        mode?: ConversationStepMode;
        name?: string;
        attachmentIds?: string[];
        skip_analysis?: boolean;
        llmFamily?: string;
      }>
    ) => {
      state.session_id = uuidv4();
      state.mode = action.payload.mode || 'clarify';
      state.init_message = action.payload.init_message;
      // Create conversation name from the message or use provided name
      state.name = action.payload.name || action.payload.init_message.slice(0, 50) + (action.payload.init_message.length > 50 ? '...' : '');
      state.messages = [];
      state.isLoading = false;
      state.isActive = true;
      state.conversationId = undefined; // Reset conversation ID
      state.skip_analysis = action.payload.skip_analysis || false;
      state.llmFamily = action.payload.llmFamily || 'Gemini';
      // If there are attachments, add them to the initial conversation state
      if (action.payload.attachmentIds && action.payload.attachmentIds.length > 0) {
        state.initialAttachmentIds = action.payload.attachmentIds;
      } else {
        state.initialAttachmentIds = undefined;
      }
    },
    resetConversation: (state) => {
      state.session_id = uuidv4();
      state.mode = 'clarify';
      state.init_message = '';
      state.name = '';
      state.messages = [];
      state.isLoading = false;
      state.isActive = false;
      state.conversationId = undefined;
      state.initialAttachmentIds = undefined;
      state.skip_analysis = false;
      state.llmFamily = 'Gemini'; // Always reset to the default model
      state.isOnboardingPrototyper = false;
      state.initiatedFromUncover = false;
    },
    setConversationId: (state, action: PayloadAction<string>) => {
      state.conversationId = action.payload;
    },
    setMode: (state, action: PayloadAction<ConversationStepMode>) => {
      state.mode = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setInitMessage: (state, action: PayloadAction<string>) => {
      state.init_message = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setActive: (state, action: PayloadAction<boolean>) => {
      state.isActive = action.payload;
    },
    setInitialAttachmentIds: (state, action: PayloadAction<string[] | undefined>) => {
      state.initialAttachmentIds = action.payload;
    },
    setSkipAnalysis: (state, action: PayloadAction<boolean>) => {
      state.skip_analysis = action.payload;
    },
    setOnboardingPrototyper: (state, action: PayloadAction<boolean>) => {
      state.isOnboardingPrototyper = action.payload;
    },
    setInitiatedFromUncover: (state, action: PayloadAction<boolean>) => {
      state.initiatedFromUncover = action.payload;
    },
    setLlmFamily: (state, action: PayloadAction<string>) => {
      state.llmFamily = action.payload;
    },
    // Message management
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    submitUserMessage: (state, action: PayloadAction<{ text: string; attachmentIds?: string[] }>) => {
      // Ensure we have either text content or attachments
      if (!action.payload.text.trim() && (!action.payload.attachmentIds || action.payload.attachmentIds.length === 0)) {
        return; // Don't add empty messages without attachments
      }
      
      const message: Message = {
        id: Date.now().toString(),
        content: action.payload.text,
        role: 'user',
        ...(action.payload.attachmentIds && action.payload.attachmentIds.length > 0 && {
          attachments_ids: action.payload.attachmentIds
        })
      };
      state.messages.push(message);
      state.isLoading = true;
    },
    addAIResponse: (state, action: PayloadAction<string>) => {
      const message: Message = {
        id: (Date.now() + 1).toString(),
        content: action.payload,
        role: 'assistant',
      };
      state.messages.push(message);
      state.isLoading = false;
    },
    addAIResponseWithBanner: (
      state,
      action: PayloadAction<{
        content: string;
        bannerType: 'info' | 'warning' | 'error' | 'success';
        drawType: string;
        bannerPosition?: 'top' | 'bottom';
      }>
    ) => {
      const message: Message = {
        id: (Date.now() + 1).toString(),
        content: action.payload.content,
        role: 'assistant',
        hasBanner: true,
        bannerType: action.payload.bannerType,
        drawType: action.payload.drawType,
        bannerPosition: action.payload.bannerPosition || 'bottom',
      };
      state.messages.push(message);
      state.isLoading = false;
    },
    updateAIMessage: (
      state,
      action: PayloadAction<{
        messageId: string;
        content: string;
        detectedLanguage?: 'rtl' | 'ltr';
        llmInteractionId?: string;
        prototyper_demo_link_id?: string | number;
        artifact_demo_link_id?: string | number;
        mode?: 'clarify' | 'prototyper' | 'strategy';
        hasBanner?: boolean;
        bannerType?: 'info' | 'success' | 'warning' | 'error';
        bannerPosition?: 'top' | 'bottom';
        bannerText?: string;
        thoughts?: Message['thoughts'];
        thinkingDurationMs?: number | null;
        artifact?: Message['artifact'];
        /**
         * SSE `prototype_ready` marker — the AI shipped a prototype spec
         * on this turn. Used to render an inline "View prototype" pill
         * beneath the assistant message (mirrors ArtifactPill).
         */
        hasPrototype?: boolean;
        prototypeReady?: boolean;
        prototypeSuperAgentId?: string;
        /** Buildo's product name for the prototype (the pill's title line). */
        prototypeTitle?: string | null;
      }>
    ) => {
      const message = state.messages.find((msg) => msg.id === action.payload.messageId);
      if (message) {
        message.content = action.payload.content;
        if (action.payload.detectedLanguage) {
          message.detectedLanguage = action.payload.detectedLanguage;
        }
        if (action.payload.llmInteractionId) {
          message.llmInteractionId = action.payload.llmInteractionId;
        }
        if (action.payload.prototyper_demo_link_id) {
          (message as any).prototyper_demo_link_id = action.payload.prototyper_demo_link_id;
        }
        if (action.payload.artifact_demo_link_id !== undefined && action.payload.artifact_demo_link_id !== null) {
          (message as any).artifact_demo_link_id = action.payload.artifact_demo_link_id;
        }
        if (action.payload.mode) {
          message.mode = action.payload.mode;
        }
        if (action.payload.hasPrototype) {
          (message as any).hasPrototype = true;
        }
        if (action.payload.prototypeReady) {
          (message as any).prototypeReady = true;
        }
        if (action.payload.prototypeSuperAgentId) {
          (message as any).prototypeSuperAgentId = action.payload.prototypeSuperAgentId;
        }
        if (action.payload.prototypeTitle) {
          message.prototypeTitle = action.payload.prototypeTitle;
        }
        if (typeof action.payload.hasBanner !== 'undefined') {
          message.hasBanner = action.payload.hasBanner;
        }
        if (action.payload.bannerType) {
          message.bannerType = action.payload.bannerType;
        }
        if (action.payload.bannerPosition) {
          message.bannerPosition = action.payload.bannerPosition;
        }
        if (action.payload.bannerText) {
          (message as any).bannerText = action.payload.bannerText;
        }
        if (action.payload.thoughts) {
          message.thoughts = action.payload.thoughts;
        }
        if (action.payload.thinkingDurationMs !== undefined) {
          message.thinkingDurationMs = action.payload.thinkingDurationMs;
        }
        if (action.payload.artifact !== undefined) {
          message.artifact = action.payload.artifact;
        }
      }
    },
    patchMessageByLlmInteractionId: (
      state,
      action: PayloadAction<{
        llmInteractionId: string;
        patch: Partial<Message> & Record<string, unknown>;
      }>,
    ) => {
      const message = state.messages.find(
        (msg) =>
          msg.role === 'assistant' && msg.llmInteractionId === action.payload.llmInteractionId,
      );
      if (message) {
        Object.assign(message, action.payload.patch);
      }
    },
    updateBanner: (
      state,
      action: PayloadAction<{
        messageId: string;
        bannerType: 'info' | 'success' | 'warning' | 'error';
        drawType: string;
        bannerPosition?: 'top' | 'bottom';
      }>
    ) => {
      const message = state.messages.find((msg) => msg.id === action.payload.messageId);
      if (message) {
        message.bannerType = action.payload.bannerType;
        message.drawType = action.payload.drawType;
        message.hasBanner = true;
        message.bannerPosition = action.payload.bannerPosition || 'bottom';
      }
    },
    updateMessageFeedback: (
      state,
      action: PayloadAction<{
        messageId: string;
        isLiked: boolean | null;
      }>
    ) => {
      const message = state.messages.find((msg) => msg.id === action.payload.messageId);
      if (message) {
        message.isLiked = action.payload.isLiked;
      }
    },
    cleanBanner: (state, action: PayloadAction<string>) => {
      const message = state.messages.find((msg) => msg.id === action.payload);
      if (message) {
        message.bannerType = undefined;
        message.drawType = undefined;
        message.hasBanner = false;
        message.bannerPosition = undefined;
      }
    },
    cleanMessages: (state) => {
      state.messages = [];
    },
    /**
     * Drop messages by id. Used to roll back the optimistic pair (the founder's
     * selection + the empty assistant placeholder) when a turn is rejected
     * before it ever started — e.g. an interactive answer the backend 400s,
     * which is surfaced on the question card instead.
     */
    removeMessages: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      state.messages = state.messages.filter((m) => !ids.has(m.id));
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },
    // Initialize conversation from params
    initializeConversation: (state, action: PayloadAction<CreateConversationParams>) => {
      state.session_id = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      state.init_message = action.payload.init_message;
      state.mode = action.payload.mode;
      state.isActive = true;
      state.initialAttachmentIds = action.payload.attachmentIds;
      state.skip_analysis = action.payload.skip_analysis || false;
      // Default to Gemini if not provided; migrate removed DeepSeek to it.
      state.llmFamily = action.payload.llmFamily === 'DeepSeek' || !action.payload.llmFamily
        ? 'Gemini'
        : action.payload.llmFamily;
      state.messages = [];
    },
  },
});

export const {
  createNewConversation,
  resetConversation,
  setConversationId,
  setMode,
  setName,
  setInitMessage,
  setLoading,
  setActive,
  setInitialAttachmentIds,
  setSkipAnalysis,
  setLlmFamily,
  setOnboardingPrototyper,
  setInitiatedFromUncover,
  addMessage,
  submitUserMessage,
  addAIResponse,
  addAIResponseWithBanner,
  updateAIMessage,
  patchMessageByLlmInteractionId,
  updateBanner,
  updateMessageFeedback,
  cleanBanner,
  cleanMessages,
  removeMessages,
  setMessages,
  initializeConversation,
} = conversationSlice.actions;

// Selectors (with defensive checks)
export const selectConversationId = (state: { conversation: ConversationState }) => state.conversation?.conversationId ?? undefined;
export const selectConversationMode = (state: { conversation: ConversationState }) => state.conversation?.mode ?? 'clarify';
export const selectConversationName = (state: { conversation: ConversationState }) => state.conversation?.name ?? '';
export const selectConversationMessages = (state: { conversation: ConversationState }) => state.conversation?.messages ?? [];
export const selectConversationIsLoading = (state: { conversation: ConversationState }) => state.conversation?.isLoading ?? false;
export const selectConversationIsActive = (state: { conversation: ConversationState }) => state.conversation?.isActive ?? false;
export const selectConversationState = (state: { conversation: ConversationState }) => state.conversation ?? initialState;
export const selectConversationLlmFamily = (state: { conversation: ConversationState }) => state.conversation?.llmFamily ?? 'Gemini';
export const selectIsOnboardingPrototyper = (state: { conversation: ConversationState }) => state.conversation?.isOnboardingPrototyper ?? false;
export const selectInitiatedFromUncover = (state: { conversation: ConversationState }) => state.conversation?.initiatedFromUncover ?? false;

export default conversationSlice.reducer;
